import { useTranslation } from 'next-i18next'
import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import AlreadyStarted from '~/components/v2/dialogs/AlreadyStarted'
import AuthDialog from '~/components/v2/dialogs/Auth'
import EarlyDialog from '~/components/v2/dialogs/Early'
import HelpDialog from '~/components/v2/dialogs/Help'
import ImpactQuestionsDialog from '~/components/v2/dialogs/ImpactQuestions'
import SettingsDialog from '~/components/v2/dialogs/Settings'
import {
  ClassImpactQuestions,
  CurrentUser,
  GetsetupInfo,
  LobbySession,
  LobbyVodClass,
} from '~/shared/api-generated-types'

enum Actions {
  HIDE_ALREADY_STARTED_DIALOG,
  TOGGLE_ALREADY_STARTED_DIALOG,
  TOGGLE_AUTH_DIALOG,
  TOGGLE_EARLY_DIALOG,
  TOGGLE_HELP_DIALOG,
  TOGGLE_SETTINGS_DIALOG,
  TOGGLE_PRECLASS_QUESTIONS_DIALOG,
  TOGGLE_POSTCLASS_QUESTIONS_DIALOG,
}

type ContextType = {
  showAlreadyStartedDialog: boolean
  showAuthDialog: boolean
  showEarlyDialog: boolean
  showHelpDialog: boolean
  showSettingsDialog: boolean
  showPreClassQuestionsDialog: boolean
  showPostClassQuestionsDialog: boolean
  hideAlreadyStartedDialog?: () => void
  toggleAlreadyStartedDialog?: () => void
  toggleAuthDialog?: () => void
  toggleEarlyDialog?: () => void
  toggleHelpDialog?: () => void
  toggleSettingsDialog?: () => void
  togglePreClassQuestionsDialog?: () => void
  togglePostClassQuestionsDialog?: () => void
}

const contextDefaultValues: ContextType = {
  showAlreadyStartedDialog: false,
  showAuthDialog: false,
  showEarlyDialog: false,
  showHelpDialog: false,
  showSettingsDialog: false,
  showPreClassQuestionsDialog: true,
  showPostClassQuestionsDialog: false,
}

const DialogContext = createContext<Partial<ContextType>>(contextDefaultValues)

type Props = {
  children: ReactNode
  currentUser: CurrentUser
  getsetupInfo: GetsetupInfo
  isAuthSuccess?: boolean
  lobbySession?: LobbySession
  lobbyVodClass?: LobbyVodClass
  classImpactQuestions?: ClassImpactQuestions
}

// Reducer function for AppState
function dialogReducer(state: ContextType, action: Actions): ContextType {
  switch (action) {
    case Actions.HIDE_ALREADY_STARTED_DIALOG:
      return { ...state, showAlreadyStartedDialog: false }

    case Actions.TOGGLE_ALREADY_STARTED_DIALOG:
      return { ...state, showAlreadyStartedDialog: !state.showAlreadyStartedDialog }

    case Actions.TOGGLE_AUTH_DIALOG:
      return { ...state, showAuthDialog: !state.showAuthDialog }

    case Actions.TOGGLE_EARLY_DIALOG:
      return { ...state, showEarlyDialog: !state.showEarlyDialog }

    case Actions.TOGGLE_HELP_DIALOG:
      return { ...state, showHelpDialog: !state.showHelpDialog }

    case Actions.TOGGLE_SETTINGS_DIALOG:
      return { ...state, showSettingsDialog: !state.showSettingsDialog }

    case Actions.TOGGLE_PRECLASS_QUESTIONS_DIALOG:
      return { ...state, showPreClassQuestionsDialog: !state.showPreClassQuestionsDialog }

    case Actions.TOGGLE_POSTCLASS_QUESTIONS_DIALOG:
      return { ...state, showPostClassQuestionsDialog: !state.showPostClassQuestionsDialog }

    default:
      return state
  }
}

export function DialogProvider({
  children,
  currentUser,
  getsetupInfo,
  isAuthSuccess,
  lobbySession,
  classImpactQuestions,
}: Props): JSX.Element {
  const { t } = useTranslation('lobby')
  const [state, dispatch] = useReducer(dialogReducer, contextDefaultValues)

  function hideAlreadyStartedDialog(): void {
    dispatch(Actions.HIDE_ALREADY_STARTED_DIALOG)
  }

  function toggleAlreadyStartedDialog(): void {
    dispatch(Actions.TOGGLE_ALREADY_STARTED_DIALOG)
  }

  function toggleAuthDialog(): void {
    dispatch(Actions.TOGGLE_AUTH_DIALOG)
  }

  function toggleEarlyDialog(): void {
    dispatch(Actions.TOGGLE_EARLY_DIALOG)
  }

  function toggleHelpDialog(): void {
    dispatch(Actions.TOGGLE_HELP_DIALOG)
  }

  function toggleSettingsDialog(): void {
    dispatch(Actions.TOGGLE_SETTINGS_DIALOG)
  }

  function togglePreClassQuestionsDialog(): void {
    dispatch(Actions.TOGGLE_PRECLASS_QUESTIONS_DIALOG)
  }

  function togglePostClassQuestionsDialog(): void {
    dispatch(Actions.TOGGLE_POSTCLASS_QUESTIONS_DIALOG)
  }

  // Handle auth success
  useEffect(() => {
    if (!isAuthSuccess || !currentUser.authenticatedUser?.firstName) return
    toast.success(t('loggedInAsUser', { name: currentUser.authenticatedUser.firstName }), { duration: 5000 })
  }, [isAuthSuccess])

  return (
    <DialogContext.Provider
      value={{
        hideAlreadyStartedDialog,
        toggleAlreadyStartedDialog,
        toggleAuthDialog,
        toggleEarlyDialog,
        toggleHelpDialog,
        toggleSettingsDialog,
        togglePreClassQuestionsDialog,
        togglePostClassQuestionsDialog,
      }}
    >
      {lobbySession && state.showAlreadyStartedDialog && <AlreadyStarted title={lobbySession.title} />}
      {lobbySession && state.showSettingsDialog && <SettingsDialog lobbySession={lobbySession} />}
      {state.showAuthDialog && <AuthDialog />}
      {state.showEarlyDialog && <EarlyDialog />}
      {state.showHelpDialog && <HelpDialog videoAsset={getsetupInfo.videoGuideWelcome} />}
      {state.showPreClassQuestionsDialog &&
        currentUser.authenticatedUser?.id &&
        classImpactQuestions?.classId &&
        classImpactQuestions?.preClassQuestions.length && (
          <ImpactQuestionsDialog
            classId={classImpactQuestions.classId}
            sessionId={lobbySession?.id}
            questionType="PRECLASS"
            question={classImpactQuestions.preClassQuestions.at(0)}
            onClose={togglePreClassQuestionsDialog}
          />
        )}
      {state.showPostClassQuestionsDialog &&
        currentUser.authenticatedUser?.id &&
        classImpactQuestions?.classId &&
        classImpactQuestions?.postClassQuestions.length && (
          <ImpactQuestionsDialog
            classId={classImpactQuestions.classId}
            sessionId={lobbySession?.id}
            questionType="POSTCLASS"
            question={classImpactQuestions.postClassQuestions.at(0)}
            onClose={togglePostClassQuestionsDialog}
          />
        )}
      <Toaster />
      {children}
    </DialogContext.Provider>
  )
}

export function useDialog(): Partial<ContextType> {
  return useContext(DialogContext)
}
